import { getRolloutTargetName } from './rollout';
import { ChangelogState } from '@/common/const';
import { ModifiedRolloutTargetMapValue } from '@/common/dto/changelog';
import { Rollout, RolloutTarget } from '@/lib/PlannerDTOs';
import {
  DiffEditType,
  getNewDefaultEditState,
  getNewEditState,
  getNewReorderState,
  getOldDefaultEditState,
  getOldEditState,
} from '@/pages/RolloutTab/diffUtils';

export function getRolloutTargetIndex(
  value: ModifiedRolloutTargetMapValue,
): number {
  const { before, after, state } = value;

  let index: number = -1;

  switch (state) {
    case ChangelogState.ADDED:
    case ChangelogState.MODIFIED:
      index = after.rollout?.rollouts?.findIndex(
        (rolloutTarget) => rolloutTarget?.id === after.rolloutTarget?.id,
      );
      break;
    case ChangelogState.NONE:
      index = after.rollout?.rollouts?.findIndex(
        (rolloutTarget) => rolloutTarget?.id === before.rolloutTarget?.id,
      );
      break;
    case ChangelogState.DELETED:
      index = before.rollout?.rollouts?.findIndex(
        (rolloutTarget) => rolloutTarget?.id === before.rolloutTarget?.id,
      );
      break;
  }

  if (index == null) index = -1;

  return index > -1 ? index + 1 : index;
}

export function getTitle(value: ModifiedRolloutTargetMapValue): string {
  const { before, after, state } = value;
  const index = getRolloutTargetIndex(value);

  if (!before.rolloutTarget && !after.rolloutTarget)
    return 'Rollout Event Tracking';

  if (index < 0) return 'Everything Else';

  switch (state) {
    case ChangelogState.ADDED:
      return getRolloutTargetName(after.rolloutTarget?.constraints || []);
    case ChangelogState.NONE:
    case ChangelogState.DELETED:
    case ChangelogState.MODIFIED:
      return getRolloutTargetName(before.rolloutTarget?.constraints || []);
  }
}

function getChangeLogItemState(diffEditType: DiffEditType) {
  switch (diffEditType) {
    case 'modified':
    case 'reordered':
      return ChangelogState.MODIFIED;
    case 'added':
      return ChangelogState.ADDED;
    case 'deleted':
      return ChangelogState.DELETED;
    default:
      return ChangelogState.NONE;
  }
}

function findMatchingTarget(
  rollout: Rollout,
  targetToFind: RolloutTarget,
): RolloutTarget | null {
  if (!rollout?.rollouts || !targetToFind?.id) {
    return null;
  }
  return (
    rollout.rollouts.find((rollout) => {
      return rollout?.id === targetToFind?.id;
    }) || null
  );
}

export function getModifiedRolloutTargetsMap(
  newRollout: Rollout,
  oldRollout: Rollout,
  includeUnchanged?: boolean,
) {
  const finalModifiedRolloutTargets = new Map<
    string,
    ModifiedRolloutTargetMapValue
  >();

  // Process old rollout targets to find deleted ones
  if (oldRollout?.rollouts) {
    oldRollout.rollouts.forEach(
      (rolloutTarget: RolloutTarget, targetIndex: number) => {
        if (!rolloutTarget?.id) return;

        const newRollouts = newRollout?.rollouts || [];
        const oldState = getOldEditState(rolloutTarget, newRollouts);
        const state = getChangeLogItemState(oldState as DiffEditType);

        // If this target is deleted, add it to the final map
        if (state === ChangelogState.DELETED) {
          const key = `deleted-${rolloutTarget.id}-${targetIndex}`;
          finalModifiedRolloutTargets.set(key, {
            state,
            before: {
              index: targetIndex,
              rollout: oldRollout,
              rolloutTarget: rolloutTarget,
            },
            after: {
              rollout: newRollout,
              rolloutTarget: null,
            },
          });
        }
      },
    );
  }

  // Process new rollout targets
  if (newRollout?.rollouts) {
    newRollout.rollouts.forEach(
      (rolloutTarget: RolloutTarget, rolloutTargetIndex: number) => {
        if (!rolloutTarget?.id) return;

        const oldRollouts = oldRollout?.rollouts || [];
        let newState = getNewEditState(rolloutTarget, oldRollouts);

        if (
          newState === '' &&
          getNewReorderState(rolloutTarget, rolloutTargetIndex, oldRollouts)
        ) {
          newState = 'reordered';
        }

        const state = getChangeLogItemState(newState as DiffEditType);

        // Skip unchanged items if not including unchanged
        if (state === ChangelogState.NONE && !includeUnchanged) {
          return;
        }

        const key = `${rolloutTarget.id}-${state}`;
        finalModifiedRolloutTargets.set(key, {
          state,
          before: {
            rollout: oldRollout,
            rolloutTarget:
              state === ChangelogState.ADDED
                ? null
                : findMatchingTarget(oldRollout, rolloutTarget),
          },
          after: {
            rollout: newRollout,
            rolloutTarget: rolloutTarget,
          },
        });
      },
    );
  }

  const oldDefaultState = getChangeLogItemState(
    getOldDefaultEditState(oldRollout?.default, newRollout?.default),
  );
  const newDefaultState = getChangeLogItemState(
    getNewDefaultEditState(oldRollout?.default, newRollout?.default),
  );

  if (
    oldDefaultState === ChangelogState.MODIFIED ||
    newDefaultState === ChangelogState.MODIFIED
  )
    finalModifiedRolloutTargets.set(`__default__-${ChangelogState.MODIFIED}`, {
      state: ChangelogState.MODIFIED,
      before: {
        rollout: oldRollout,
        rolloutTarget: oldRollout?.default,
      },
      after: {
        rollout: newRollout,
        rolloutTarget: newRollout?.default,
      },
    });

  if (includeUnchanged) {
    if (
      oldDefaultState === ChangelogState.NONE ||
      newDefaultState === ChangelogState.NONE
    ) {
      finalModifiedRolloutTargets.set(`__default__-${ChangelogState.NONE}`, {
        state: ChangelogState.NONE,
        before: {
          rollout: oldRollout,
          rolloutTarget: oldRollout?.default,
        },
        after: {
          rollout: newRollout,
          rolloutTarget: newRollout?.default,
        },
      });
    }
  }

  let mode, limit, oldMode, oldLimit;

  if (newRollout?.trackConf) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    const { mode: newMode, limit: newLimit } = newRollout?.trackConf;

    mode = newMode;
    limit = newLimit;
  }

  if (oldRollout?.trackConf) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    const { mode: oldTrackMode, limit: oldTrackLimit } = oldRollout?.trackConf;

    oldMode = oldTrackMode;
    oldLimit = oldTrackLimit;
  }

  if (mode !== oldMode || limit !== oldLimit)
    finalModifiedRolloutTargets.set(`__trackConf__-modified`, {
      state: ChangelogState.MODIFIED,
      before: {
        rollout: oldRollout,
      },
      after: {
        rollout: newRollout,
      },
    });

  return finalModifiedRolloutTargets;
}
